


























































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";

import {
  AdcontrolProfile,
  Brand,
  Company,
  Media,
  Product,
  User,
} from "@/models";
import InvestorsMixins from "@/mixins/http/Investor";

import ColorHelper from "@/services/ColorHelper";

import VListGroup, { VListGroupItem } from "@/components/VListGroup.vue";
import { SlotObject } from "@/components/TimeSlotPicker.vue";
import EntryItem from "@/components/EntryItem.vue";
import TopListItemValue from "./TopListItemValue.vue";

const requestTag = "top-list-filter";

export interface TopListFilter {
  media: {
    radio: boolean;
    tv: boolean;
  };
  date: {
    from: string;
    to: string;
  };
  compare?: {
    from: string;
    to: string;
  };
  grpTarget: string | null;
  groupBy: string;
  taxonomy: {
    sector?: string | string[];
    category?: string | string[];
    class?: string | string[];
  };
  timeSlots?: SlotObject[];
  detectionChoice?: string;
  grpChoice?: string;
}

export type UnitOfMeasure =
  | "spots"
  | "seconds"
  | "grp"
  | "grp_over_spots"
  | "audience"
  | "ebiquity_min"
  | "ebiquity_max";

export type TotalsTargetFields = "detectionsCount" | "vodDetectionsCount";

@Component({
  components: {
    VListGroup,
    EntryItem,
    TopListItemValue,
  },
})
export default class TopList extends Mixins(InvestorsMixins) {
  @Prop() readonly filter!: TopListFilter;
  @Prop({ default: "count" }) readonly type?: string;
  @Prop({ default: true }) readonly ordered?: boolean;
  @Prop({ default: false }) readonly selectable?: boolean;
  @Prop({ default: false }) readonly shareOfVoice?: boolean;
  @Prop({ default: 100 }) readonly maxItems?: number;
  @Prop() readonly unitOfMeasure?: UnitOfMeasure;
  @Prop() readonly isOver?: Function;
  @Prop({ default: null }) readonly adcontrolProfile?: AdcontrolProfile | null;

  @Getter("user") user!: User;

  @Watch("filter", { deep: true })
  onFilterChange() {
    this.load();
  }

  @Watch("$store.state.settings.profile")
  onProfileChanged() {
    this.load();
  }

  @Watch("type")
  onTypeChanged() {
    this.load();
  }

  items: Array<Company | Brand | Product> = [];
  loading = false;

  get disablePopover(): boolean {
    return !(this.filter.media.radio && this.filter.media.tv);
  }

  get onlyVodProfile(): boolean {
    return this.adcontrolProfile?.mediaSourceKind === "msk_vod";
  }

  mounted() {
    this.load();
  }

  async load() {
    if (this.filter == undefined) {
      return;
    }

    this.loading = true;

    const media = Media.mediaToList(this.filter.media);

    const data = {
      page: {
        size: this.maxItems,
        number: 1,
      },
      filter: {
        media: media,
        time_slots: this.filter.timeSlots,
        date_range: [this.filter.date.from, this.filter.date.to],
        sector: this.filter.taxonomy?.sector
          ? Array.isArray(this.filter.taxonomy?.sector)
            ? this.filter.taxonomy?.sector
            : [this.filter.taxonomy.sector]
          : [],
        category: this.filter.taxonomy?.category
          ? Array.isArray(this.filter.taxonomy?.category)
            ? this.filter.taxonomy?.category
            : [this.filter.taxonomy.category]
          : [],
        class: this.filter.taxonomy?.class
          ? Array.isArray(this.filter.taxonomy?.class)
            ? this.filter.taxonomy?.class
            : [this.filter.taxonomy.class]
          : [],
        grp_target: this.filter.grpTarget,
      },
      type: this.type,
      vod: this.onlyVodProfile,
    };

    const items = await this.getChartEntries(
      this.filter.groupBy,
      data,
      requestTag
    );

    await this.loadCompare(items, data);

    const total = this.computeItemsTotal(
      items,
      this.onlyVodProfile ? "vodDetectionsCount" : "detectionsCount"
    );
    this.items = items.map((company: Company) => {
      const detectionsCount =
        (this.onlyVodProfile
          ? company.vodDetectionsCount
          : company.detectionsCount) || 0;
      company.shareOfVoice = (detectionsCount / total) * 100;
      return company;
    });

    this.loading = false;
  }

  // eslint-disable-next-line
  async loadCompare(items: Array<Company>, data: any) {
    if (this.filter.compare == undefined) {
      return;
    }

    const ids = items.map((company: Company) => {
      return company.id;
    });

    data.filter["date_range"] = [
      this.filter.compare.from,
      this.filter.compare.to,
    ];
    data.filter["ids"] = ids;

    const compare = await this.getChartEntries(
      this.filter.groupBy,
      data,
      requestTag
    );

    const detections = new Map();
    compare.map((company: Company) => {
      detections.set(company.id, {
        detectionsCount: company.detectionsCount,
        vodDetectionsCount: company.vodDetectionsCount,
      });
    });

    items.map((company: Company) => {
      if (detections.has(company.id)) {
        company.detectionsCountCompare = detections.get(
          company.id
        ).detectionsCount;
        company.vodDetectionsCountCompare = detections.get(
          company.id
        ).vodDetectionsCount;
      } else {
        company.detectionsCountCompare = undefined;
        company.vodDetectionsCountCompare = undefined;
      }

      return company;
    });
  }

  // eslint-disable-next-line
  isHighlighted(item: any) {
    if (!this.user?.profile?.company) {
      return false;
    }

    const id = this.user.profile.company.id;

    if (this.filter.groupBy == "companies") {
      return item.id == id;
    } else if (item.company) {
      return item.company.id == id;
    }

    return false;
  }

  getColor(id: string): string {
    return ColorHelper.pickForKey(id);
  }

  getValueDelta(value: Brand | Product | Company): number | undefined {
    return this.onlyVodProfile
      ? value.vodDetectionsCountDelta
      : value.detectionsCountDelta;
  }

  getItemPercentageClass(value: number | undefined) {
    return {
      "item-percentage-negative": value && value < 0,
      "item-percentage-positive": value && value > 0,
      "badge badge-color-6": value === undefined,
    };
  }

  onItemOver(item: Company | undefined) {
    this.$emit("over", item);
  }

  onEntryClick(item: Company | Brand | Product) {
    this.$router.push({
      name: "InvestorSummary",
      params: {
        type: item.type,
        id: item.id,
      },
      query: {
        from: this.filter.date.from,
        to: this.filter.date.to,
        radio: this.filter.media.radio ? "1" : "0",
        tv: this.filter.media.tv ? "1" : "0",
        unit: this.unitOfMeasure,
      },
    });
  }

  computeItemsTotal(
    items: Array<Company | Brand | Product>,
    targetField: TotalsTargetFields
  ): number {
    return items.reduce(
      (accumulator: number, item: Company | Brand | Product) => {
        let value: number;
        if (!item[targetField]) {
          return accumulator;
        }
        if (typeof item[targetField] === "number") {
          value = (item[targetField] as number) || 0;
        } else {
          value = parseFloat(item[targetField] as unknown as string) || 0.0;
        }
        return (accumulator + value) as number;
      },
      0
    );
  }
}
